import { useMemo, useCallback } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useObjectives } from "@/hooks/use-objectives";
import { useFocusAreas } from "@/hooks/use-focus-areas";
import { useTactics } from "@/hooks/use-tactics";
import { useStrategicPlans } from "@/hooks/use-strategic-plans";
import {
  LEVEL_RANK,
  LEVEL_LABELS,
  ORDERED_LEVELS,
  type ObjectiveLevel,
} from "@/lib/objective-levels";

const NONE_VALUE = "__none__";

interface AlignmentFieldsProps {
  level: ObjectiveLevel;
  parentObjectiveId: string | null;
  focusAreaId: string | null;
  tacticId: string | null;
  onParentObjectiveIdChange: (id: string | null) => void;
  onFocusAreaIdChange: (id: string | null) => void;
  onTacticIdChange: (id: string | null) => void;
  isDepartmentHead?: boolean;
  excludeObjectiveId?: string;
  disabled?: boolean;
}

export function AlignmentFields({
  level,
  parentObjectiveId,
  focusAreaId,
  tacticId,
  onParentObjectiveIdChange,
  onFocusAreaIdChange,
  onTacticIdChange,
  isDepartmentHead = false,
  excludeObjectiveId,
  disabled = false,
}: AlignmentFieldsProps) {
  const canHaveParent = level !== "department" && !(isDepartmentHead && level === "individual");

  return (
    <>
      {canHaveParent && (
        <ParentObjectiveSelect
          level={level}
          selectedId={parentObjectiveId}
          onChange={onParentObjectiveIdChange}
          excludeObjectiveId={excludeObjectiveId}
          disabled={disabled}
        />
      )}
      <StrategicAlignmentSelects
        focusAreaId={focusAreaId}
        tacticId={tacticId}
        onFocusAreaIdChange={onFocusAreaIdChange}
        onTacticIdChange={onTacticIdChange}
        disabled={disabled}
      />
    </>
  );
}

// --- Parent Objective Select (single-select) ---

interface ParentOptionGroup {
  level: string;
  items: Array<{ value: string; label: string }>;
}

interface ParentObjectiveSelectProps {
  level: ObjectiveLevel;
  selectedId: string | null;
  onChange: (id: string | null) => void;
  excludeObjectiveId?: string;
  disabled?: boolean;
}

function ParentObjectiveSelect({
  level,
  selectedId,
  onChange,
  excludeObjectiveId,
  disabled = false,
}: ParentObjectiveSelectProps) {
  const { data: allObjectivesData } = useObjectives({});
  const allObjectives = allObjectivesData?.data ?? [];

  const groups = useMemo(() => {
    const currentRank = LEVEL_RANK[level];
    return buildParentGroups(allObjectives, currentRank, excludeObjectiveId);
  }, [allObjectives, level, excludeObjectiveId]);

  const handleChange = (value: string) => {
    onChange(value === NONE_VALUE ? null : value);
  };

  if (disabled) {
    const label = resolveGroupLabel(selectedId, groups);
    return (
      <div className="space-y-2">
        <Label>Parent Objective</Label>
        <p className="text-sm">{label}</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label>Parent Objective</Label>
      <Select value={selectedId ?? NONE_VALUE} onValueChange={handleChange}>
        <SelectTrigger>
          <SelectValue placeholder="None" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>None</SelectItem>
          {groups.map((group) => (
            <SelectGroup key={group.level}>
              <SelectLabel>{group.level}</SelectLabel>
              {group.items.map((item) => (
                <SelectItem key={item.value} value={item.value}>
                  {item.label}
                </SelectItem>
              ))}
            </SelectGroup>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function buildParentGroups(
  objectives: Array<{ id: string; title: string; level: string; owner_name?: string }>,
  currentRank: number,
  excludeId?: string,
): ParentOptionGroup[] {
  const result: ParentOptionGroup[] = [];
  for (const lvl of ORDERED_LEVELS) {
    if (LEVEL_RANK[lvl] !== currentRank - 1) continue;
    const items: ParentOptionGroup["items"] = [];
    for (const o of objectives) {
      if (o.level !== lvl) continue;
      if (excludeId && o.id === excludeId) continue;
      items.push({
        value: o.id,
        label: `${o.title} — ${o.owner_name ?? "Unknown"}`,
      });
    }
    if (items.length > 0) {
      result.push({ level: LEVEL_LABELS[lvl], items });
    }
  }
  return result;
}

function resolveGroupLabel(
  selectedId: string | null,
  groups: ParentOptionGroup[],
): string {
  if (!selectedId) return "None";
  for (const group of groups) {
    const match = group.items.find((item) => item.value === selectedId);
    if (match) return match.label;
  }
  return "None";
}

// --- Strategic Alignment Selects (Focus Area + Tactic single-select) ---

interface StrategicAlignmentSelectsProps {
  focusAreaId: string | null;
  tacticId: string | null;
  onFocusAreaIdChange: (id: string | null) => void;
  onTacticIdChange: (id: string | null) => void;
  disabled?: boolean;
}

function StrategicAlignmentSelects({
  focusAreaId,
  tacticId,
  onFocusAreaIdChange,
  onTacticIdChange,
  disabled = false,
}: StrategicAlignmentSelectsProps) {
  const { data: plansData } = useStrategicPlans();
  const { data: focusAreasData } = useFocusAreas();
  const { data: tacticsData } = useTactics({});
  const plans = plansData?.data ?? [];
  const allFocusAreas = focusAreasData?.data ?? [];
  const allTactics = tacticsData?.data ?? [];

  const focusAreaGroups = useMemo(
    () => buildFocusAreaGroups(allFocusAreas, plans),
    [allFocusAreas, plans],
  );

  const tacticItems = useMemo(
    () => buildTacticItems(allTactics, focusAreaId),
    [allTactics, focusAreaId],
  );

  const handleFocusAreaChange = useCallback(
    (value: string) => {
      const newId = value === NONE_VALUE ? null : value;
      onFocusAreaIdChange(newId);
      // Clear tactic if it doesn't belong to the new focus area
      if (tacticId) {
        const tactic = allTactics.find((t) => t.id === tacticId);
        if (tactic && tactic.focus_area_id !== newId) {
          onTacticIdChange(null);
        }
      }
    },
    [tacticId, allTactics, onFocusAreaIdChange, onTacticIdChange],
  );

  const handleTacticChange = useCallback(
    (value: string) => {
      onTacticIdChange(value === NONE_VALUE ? null : value);
    },
    [onTacticIdChange],
  );

  if (disabled) {
    return (
      <div className="grid grid-cols-2 gap-4">
        <ReadOnlyField
          label="ITS Focus Area"
          value={resolveItemLabel(focusAreaId, focusAreaGroups)}
        />
        <ReadOnlyField
          label="ITS Strategic Initiative"
          value={resolveItemLabel(tacticId, tacticItems)}
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Label>ITS Focus Area</Label>
        <Select value={focusAreaId ?? NONE_VALUE} onValueChange={handleFocusAreaChange}>
          <SelectTrigger>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>None</SelectItem>
            {focusAreaGroups.map((group) => (
              <SelectGroup key={group.group}>
                <SelectLabel>{group.group}</SelectLabel>
                {group.items.map((item) => (
                  <SelectItem key={item.value} value={item.value}>
                    {item.label}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>ITS Strategic Initiative</Label>
        <Select
          value={tacticId ?? NONE_VALUE}
          onValueChange={handleTacticChange}
          disabled={!focusAreaId}
        >
          <SelectTrigger>
            <SelectValue placeholder="None" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE_VALUE}>None</SelectItem>
            {tacticItems.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

interface OptionGroup {
  group: string;
  items: Array<{ value: string; label: string }>;
}

function buildFocusAreaGroups(
  focusAreas: Array<{ id: string; name: string; strategic_plan_id: string }>,
  plans: Array<{ id: string; name: string }>,
): OptionGroup[] {
  const planNames = new Map(plans.map((p) => [p.id, p.name]));
  const byPlan = new Map<string, Array<{ value: string; label: string }>>();
  for (const fa of focusAreas) {
    const planName = planNames.get(fa.strategic_plan_id) ?? "Other";
    if (!byPlan.has(planName)) byPlan.set(planName, []);
    byPlan.get(planName)!.push({ value: fa.id, label: fa.name });
  }
  return Array.from(byPlan.entries()).map(([group, items]) => ({ group, items }));
}

function buildTacticItems(
  allTactics: Array<{ id: string; name: string; focus_area_id: string; reference_code?: string | null }>,
  selectedFocusAreaId: string | null,
): Array<{ value: string; label: string }> {
  if (!selectedFocusAreaId) return [];
  return allTactics
    .filter((t) => t.focus_area_id === selectedFocusAreaId)
    .map((t) => ({
      value: t.id,
      label: t.reference_code ? `${t.reference_code}: ${t.name}` : t.name,
    }));
}

// --- Shared helpers ---

function resolveItemLabel(
  id: string | null,
  source: OptionGroup[] | Array<{ value: string; label: string }>,
): string {
  if (!id) return "None";
  // Flat array
  const first = source[0];
  if (first && "value" in first) {
    const match = (source as Array<{ value: string; label: string }>).find((o) => o.value === id);
    return match?.label ?? "None";
  }
  // Grouped array
  for (const group of source as OptionGroup[]) {
    const match = group.items.find((item) => item.value === id);
    if (match) return match.label;
  }
  return "None";
}

interface ReadOnlyFieldProps {
  label: string;
  value: string;
}

function ReadOnlyField({ label, value }: ReadOnlyFieldProps) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <p className="text-sm">{value}</p>
    </div>
  );
}

/**
 * Check if a parent objective is valid for the given level.
 * Returns true if parentId is null or the parent is exactly one level above newLevel.
 */
export function isParentValidForLevel(
  parentId: string | null,
  newLevel: ObjectiveLevel,
  allObjectives: Array<{ id: string; level: string }>,
): boolean {
  if (!parentId) return true;
  const newRank = LEVEL_RANK[newLevel];
  const parent = allObjectives.find((o) => o.id === parentId);
  return !!parent && LEVEL_RANK[parent.level as ObjectiveLevel] === newRank - 1;
}
